import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Customer, Project, Payload } from '../types/api';
import {
  flightMetadataOptionsKeys,
  type FlightMetadataOptionsData,
} from './flightMetadataOptions.queries';

// STD-015: write/imperative actions for the flight-metadata-options domain.
// Each adder appends the server-returned entity to the cached options list via
// setQueryData (no refetch) — mirroring the original hook's optimistic local
// append. Errors are NOT toasted globally (skipGlobalErrorToast: true) because
// callers expect the returned Promise to throw — consistent with the previous
// useCallback behaviour.

export function useAddCustomer() {
  const queryClient = useQueryClient();
  return useMutation<Customer, Error, string>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (name: string) => apiService.createCustomer(name),
    onSuccess: (created) => {
      queryClient.setQueryData<FlightMetadataOptionsData>(
        flightMetadataOptionsKeys.options(),
        (old) => (old ? { ...old, customers: [...old.customers, created] } : old)
      );
    },
  });
}

export function useAddProject() {
  const queryClient = useQueryClient();
  return useMutation<Project, Error, string>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (name: string) => apiService.createProject(name),
    onSuccess: (created) => {
      queryClient.setQueryData<FlightMetadataOptionsData>(
        flightMetadataOptionsKeys.options(),
        (old) => (old ? { ...old, projects: [...old.projects, created] } : old)
      );
    },
  });
}

export interface AddPayloadInput {
  name: string;
  weight?: number;
}

export function useAddPayload() {
  const queryClient = useQueryClient();
  return useMutation<Payload, Error, AddPayloadInput>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ name, weight }: AddPayloadInput) => apiService.createPayload(name, weight),
    onSuccess: (created) => {
      queryClient.setQueryData<FlightMetadataOptionsData>(
        flightMetadataOptionsKeys.options(),
        (old) => (old ? { ...old, payloads: [...old.payloads, created] } : old)
      );
    },
  });
}
